import { WritableSignal } from '@angular/core';

/**
 * Gruppierungs-Helfer für die {@link AraGenericTableComponent}: erlaubt EINE Tabelle mit
 * Rollen-/Kategorie-Zwischenüberschriften statt mehrerer Einzeltabellen.
 *
 * Muster (im `tableTemplate` des Aufrufers):
 * ```html
 * <table mat-table [dataSource]="flatRows()">
 *   <!-- fachliche Spalten … -->
 *   <ng-container matColumnDef="__araGroupHeader">
 *     <td mat-cell *matCellDef="let row" [attr.colspan]="shell.displayedColumns().length">
 *       {{ row.__araGroupHeader.label }}
 *     </td>
 *   </ng-container>
 *   <tr mat-header-row *matHeaderRowDef="shell.displayedColumns()"></tr>
 *   <tr mat-row *matRowDef="let row; columns: ['__araGroupHeader']; when: isTableGroupHeader"></tr>
 *   <tr mat-row *matRowDef="let row; columns: shell.displayedColumns()"></tr>
 * </table>
 * ```
 * `flatRows()` = `flattenGroups(groups)`; `isTableGroupHeader` ist das `when`-Prädikat der Kopfzeile.
 */

/** Eine Zeilen-Gruppe (Zwischenüberschrift + zugehörige Datenzeilen). */
export interface TableGroup<T> {
	/** Stabiler Schlüssel (Tracking + Identität der Kopfzeile). */
	key: string;
	/** Bereits übersetzter Kopfzeilen-Text (das Header-Template darf `meta` für mehr nutzen). */
	label: string;
	/** Datenzeilen dieser Gruppe. */
	rows: T[];
	/** Optionale Nutzlast für das Kopfzeilen-Template (z. B. der Rollentyp). */
	meta?: unknown;
}

/** Marker-Zeile für eine Gruppen-Kopfzeile in der flachen Datasource. */
export interface TableGroupHeaderRow<T> {
	readonly __araGroupHeader: TableGroup<T>;
}

/** Marker-Zeile für die „neue Zeile hinzufügen"-Zeile am Ende einer Gruppe. */
export interface TableGroupAddRow<T> {
	readonly __araGroupAdd: TableGroup<T>;
}

/** Marker-Zeile für „Alle anzeigen / weniger anzeigen" am Ende einer gekürzten Gruppe. */
export interface TableGroupMoreRow<T> {
	readonly __araGroupMore: {
		readonly group: TableGroup<T>;
		readonly hiddenCount: number;
		readonly expanded: boolean;
	};
}

/** Eine Zeile der flachen Datasource: Gruppen-Kopfzeile, Mehr-/Add-Zeile oder Datenzeile. */
export type GroupedRow<T> = TableGroupHeaderRow<T> | TableGroupAddRow<T> | TableGroupMoreRow<T> | T;

/**
 * Spalten-IDs der Marker-Zeilen. `satisfies keyof …` bindet sie an die Marker-Interfaces: `mat-table`
 * drückt eine ZEILENART über einen `matColumnDef`-Namen aus, deshalb müssen Spaltenname und Interface-Key
 * zwingend identisch bleiben — ein Rename bricht hier zur Compile-Zeit statt still zur Laufzeit.
 */
export const ARA_GROUP_HEADER = '__araGroupHeader' satisfies keyof TableGroupHeaderRow<unknown>;
export const ARA_GROUP_ADD = '__araGroupAdd' satisfies keyof TableGroupAddRow<unknown>;
export const ARA_GROUP_MORE = '__araGroupMore' satisfies keyof TableGroupMoreRow<unknown>;

/**
 * Fertige `*matRowDef`-Bausteine der drei Marker-Zeilen (Spalten-IDs + `when`-Prädikat). Der Consumer
 * bindet sie an EINE Feld-Referenz und schreibt nur noch drei Ein-Zeilen-Row-Defs — die Zellen selbst
 * liefert {@link AraTableGroupColumnsComponent}. Row-Defs bleiben BEWUSST beim Consumer: eine Kind-View
 * kann einer `mat-table` keine `*matRowDef`s mit `when` sauber zur Laufzeit unterschieben (der Daten-
 * Differ der Tabelle rendert dann vor der Registrierung), Spalten hingegen schon (Muster: `MatTextColumn`).
 */
export const ARA_GROUP_ROW_DEFS = {
	header: { columns: [ARA_GROUP_HEADER], when: isTableGroupHeader },
	more: { columns: [ARA_GROUP_MORE], when: isTableGroupMoreRow },
	add: { columns: [ARA_GROUP_ADD], when: isTableGroupAddRow },
} as const;

/** `when`-Prädikat für die Gruppen-Kopfzeile (`*matRowDef … when: isTableGroupHeader`). */
export function isTableGroupHeader<T>(_index: number, row: GroupedRow<T>): row is TableGroupHeaderRow<T> {
	return !!row && typeof row === 'object' && '__araGroupHeader' in row;
}

/** `when`-Prädikat für die Add-Zeile einer Gruppe (`*matRowDef … when: isTableGroupAddRow`). */
export function isTableGroupAddRow<T>(_index: number, row: GroupedRow<T>): row is TableGroupAddRow<T> {
	return !!row && typeof row === 'object' && '__araGroupAdd' in row;
}

/** `when`-Prädikat für die Mehr-Zeile einer Gruppe (`*matRowDef … when: isTableGroupMoreRow`). */
export function isTableGroupMoreRow<T>(_index: number, row: GroupedRow<T>): row is TableGroupMoreRow<T> {
	return !!row && typeof row === 'object' && '__araGroupMore' in row;
}

/** Optionen für {@link flattenGroups}. */
export interface FlattenGroupsOptions<T> {
	/** Schlüssel eingeklappter Gruppen: deren Datenzeilen (und Add-Zeile) werden ausgelassen. */
	collapsedKeys?: ReadonlySet<string>;
	/** Ob eine Gruppe (wenn nicht eingeklappt) eine Add-Zeile am Ende erhält; default: keine. */
	addRow?: boolean | ((group: TableGroup<T>) => boolean);
	/**
	 * Zeilen, die zusammengefasst bleiben, solange die Gruppe nicht über {@link expandedKeys}
	 * aufgeklappt ist: Zahl = ab der N-ten Zeile verbergen, Prädikat = zeilenweise entscheiden.
	 * `rows` behält immer alle Zeilen, ein Zähler-Badge zeigt also weiterhin die Gesamtzahl.
	 */
	hiddenRows?: number | ((row: T, index: number, group: TableGroup<T>) => boolean);
	/** Schlüssel der Gruppen, in denen „Alle anzeigen" aktiv ist. */
	expandedKeys?: ReadonlySet<string>;
}

/**
 * Baut die flache Datasource: je Gruppe eine Kopfzeile, gefolgt von ihren Datenzeilen, optional
 * einer Mehr-Zeile („Alle anzeigen") und einer Add-Zeile. Eingeklappte Gruppen zeigen nur ihre
 * Kopfzeile.
 */
export function flattenGroups<T>(groups: readonly TableGroup<T>[], options: FlattenGroupsOptions<T> = {}): GroupedRow<T>[] {
	const { collapsedKeys, addRow, hiddenRows, expandedKeys } = options;
	const wantsAddRow = typeof addRow === 'function' ? addRow : (): boolean => addRow === true;
	const isHidden = toHiddenPredicate(hiddenRows);
	const flat: GroupedRow<T>[] = [];
	for (const group of groups) {
		flat.push({ __araGroupHeader: group });
		if (collapsedKeys?.has(group.key)) {
			continue;
		}
		const expanded = expandedKeys?.has(group.key) ?? false;
		let hiddenCount = 0;
		group.rows.forEach((row, index) => {
			if (isHidden(row, index, group)) {
				hiddenCount++;
				// Aufgeklappt bleibt der Zähler, damit die Mehr-Zeile „weniger anzeigen" anbieten kann.
				if (expanded) {
					flat.push(row);
				}
				return;
			}
			flat.push(row);
		});
		if (hiddenCount > 0) {
			flat.push({ __araGroupMore: { group, hiddenCount, expanded } });
		}
		if (wantsAddRow(group)) {
			flat.push({ __araGroupAdd: group });
		}
	}
	return flat;
}

/** Normalisiert `hiddenRows` (Zahl, Prädikat oder nichts) auf ein einheitliches Prädikat. */
function toHiddenPredicate<T>(hiddenRows: FlattenGroupsOptions<T>['hiddenRows']): (row: T, index: number, group: TableGroup<T>) => boolean {
	if (hiddenRows == null) {
		return () => false;
	}
	if (typeof hiddenRows === 'number') {
		return (_row, index) => index >= hiddenRows;
	}
	return hiddenRows;
}

/** Sortiert die Datenzeilen INNERHALB jeder Gruppe (Gruppenreihenfolge bleibt erhalten). */
export function sortGroupRows<T>(groups: readonly TableGroup<T>[], compare: (a: T, b: T) => number): TableGroup<T>[] {
	return groups.map((group) => ({ ...group, rows: [...group.rows].sort(compare) }));
}

/** Schaltet einen Schlüssel in einem Set-Signal um (Zustand für Einklappen, „Alle anzeigen", Spalten). */
export function toggleSetKey(target: WritableSignal<ReadonlySet<string>>, key: string): void {
	target.update((current) => {
		const next = new Set(current);
		if (!next.delete(key)) {
			next.add(key);
		}
		return next;
	});
}
